import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Dumbbell, CalendarDays, Activity } from 'lucide-react';
import { adminGetGyms } from './AdminPanel';
import { supabase } from '../../lib/supabase';
import { format, parseISO } from 'date-fns';
import { maskPhone } from '../../utils/helpers';
import { ko } from 'date-fns/locale';

async function adminGetGymMembers(gymId) {
  const { data, error } = await supabase.rpc('admin_get_gym_members', { p_gym_id: gymId });
  if (error) { console.error('[adminGetGymMembers]', error); return []; }
  return data || [];
}

async function adminGetGymTrainers(gymId) {
  const { data, error } = await supabase.rpc('admin_get_gym_trainers', { p_gym_id: gymId });
  if (error) { console.error('[adminGetGymTrainers]', error); return []; }
  return data || [];
}

async function adminGetGymPtAssignments(gymId) {
  const { data, error } = await supabase.rpc('admin_get_gym_pt_assignments', { p_gym_id: gymId });
  if (error) { console.error('[adminGetGymPtAssignments]', error); return []; }
  return data || [];
}

async function adminGetGymPtSessions(gymId) {
  const { data, error } = await supabase.rpc('admin_get_gym_pt_sessions', { p_gym_id: gymId });
  if (error) { console.error('[adminGetGymPtSessions]', error); return []; }
  return data || [];
}

const STATUS_BADGE = {
  active:    'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  pending:   'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
  inactive:  'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  scheduled: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  completed: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const STATUS_LABEL = {
  active: '활성', pending: '대기', inactive: '비활성',
  scheduled: '예정', completed: '완료', cancelled: '취소',
};

export default function AdminGymDetail() {
  const { gymId } = useParams();
  const navigate = useNavigate();

  const [gym, setGym] = useState(null);
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [ptAssignments, setPtAssignments] = useState([]);
  const [ptSessions, setPtSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);


  // 헬스장 기본 정보
  useEffect(() => {
    adminGetGyms().then(gyms => {
      const found = gyms.find(g => g.id === gymId);
      setGym(found || null);
      setLoading(false);
    });
  }, [gymId]);

  const loadTab = useCallback(async (t) => {
    setTabLoading(true);
    if (t === 'members') {
      const data = await adminGetGymMembers(gymId);
      setMembers(data);
    } else if (t === 'trainers') {
      const data = await adminGetGymTrainers(gymId);
      setTrainers(data);
    } else if (t === 'pt') {
      const data = await adminGetGymPtAssignments(gymId);
      setPtAssignments(data);
    } else if (t === 'sessions') {
      const data = await adminGetGymPtSessions(gymId);
      setPtSessions(data);
    }
    setTabLoading(false);
  }, [gymId]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="space-y-4 max-w-5xl">
        <button onClick={() => navigate('/admin/gyms')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          <ArrowLeft className="w-4 h-4" /> 헬스장 목록으로
        </button>
        <p className="text-gray-400 text-center py-16">헬스장 정보를 찾을 수 없습니다.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'members', label: '회원', icon: Users },
    { id: 'trainers', label: '트레이너', icon: Dumbbell },
    { id: 'pt', label: 'PT 배정', icon: Activity },
    { id: 'sessions', label: 'PT 일정', icon: CalendarDays },
  ];

  return (
    <div className="space-y-4 max-w-5xl">
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/gyms')}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{gym.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {gym.owner_name} · {gym.code && <span className="font-mono">{gym.code}</span>}
            {' '}· 회원 {gym.member_count}명
          </p>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? 'border-red-600 text-red-700 dark:text-red-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* 콘텐츠 */}
      {tabLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* 회원 탭 */}
          {tab === 'members' && (
            members.length === 0 ? (
              <p className="text-center text-gray-400 py-16">등록된 회원이 없습니다.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {members.map(m => (
                  <div key={m.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary-700 dark:text-primary-400">{m.name[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</p>
                      <p className="text-xs text-gray-400">{maskPhone(m.phone)} {m.gender && `· ${m.gender}`}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[m.status] || STATUS_BADGE.inactive}`}>
                      {STATUS_LABEL[m.status] || m.status}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}

          {/* 트레이너 탭 */}
          {tab === 'trainers' && (
            trainers.length === 0 ? (
              <p className="text-center text-gray-400 py-16">등록된 트레이너가 없습니다.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {trainers.map(t => (
                  <div key={t.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-blue-700 dark:text-blue-400">{t.name[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{t.name}</p>
                      <p className="text-xs text-gray-400">{maskPhone(t.phone)} {t.specialty && `· ${t.specialty}`}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      t.user_id
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {t.user_id ? '계정 연동' : '미연동'}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}

          {/* PT 배정 탭 */}
          {tab === 'pt' && (
            ptAssignments.length === 0 ? (
              <p className="text-center text-gray-400 py-16">PT 배정 내역이 없습니다.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {ptAssignments.map(a => {
                  const remaining = a.total_sessions - a.used_sessions;
                  return (
                    <div key={a.id} className="px-5 py-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{a.member_name}</p>
                        <p className="text-xs text-gray-400">
                          {a.trainer_name && `${a.trainer_name} · `}{a.product_name}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-semibold ${
                          remaining <= 0 ? 'text-red-500' : remaining <= 5 ? 'text-yellow-500' : 'text-green-600'
                        }`}>
                          잔여 {remaining}회
                        </p>
                        <p className="text-xs text-gray-400">{a.used_sessions}/{a.total_sessions}회</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_BADGE[a.status] || STATUS_BADGE.inactive}`}>
                        {STATUS_LABEL[a.status] || a.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* PT 일정 탭 */}
          {tab === 'sessions' && (
            ptSessions.length === 0 ? (
              <p className="text-center text-gray-400 py-16">등록된 PT 일정이 없습니다.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {ptSessions.map(s => (
                  <div key={s.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{s.member_name}</p>
                      <p className="text-xs text-gray-400">{s.trainer_name} · {s.product_name}</p>
                      {s.notes && <p className="text-xs text-gray-400 italic mt-0.5">"{s.notes}"</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                        {format(parseISO(s.scheduled_at), 'M/d (EEE)', { locale: ko })}
                      </p>
                      <p className="text-xs text-gray-400">{format(parseISO(s.scheduled_at), 'HH:mm')}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_BADGE[s.status] || STATUS_BADGE.scheduled}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

    </div>
  );
}
